import type { DemoPersona } from "@peer-start/platform-contracts";
import { Button, Card, Modal, PageHeading, SearchField } from "@peer-start/ui";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../app/use-app-context.js";
import { usePersonas } from "../api/queries.js";
import {
  defaultUserRole,
  loadUserRoles,
  saveUserRoles,
  userEmail,
  userLastLogin,
  type ManagedUserRole,
} from "../data/user-management.js";

export function UserManagementPage() {
  const { t } = useTranslation();
  const { bootstrap, persona } = useAppContext();
  const personas = usePersonas();
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState<Record<string, ManagedUserRole>>(loadUserRoles);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<ManagedUserRole>("teacher");
  const [notice, setNotice] = useState<string | null>(null);
  if (persona.kind === "employee") return null;

  if (personas.isLoading) {
    return <p className="py-10 text-center text-muted-foreground">{t("common.loading")}</p>;
  }
  if (personas.isError || !personas.data) {
    return <p className="py-10 text-center text-destructive">{t("common.error")}</p>;
  }

  const managedUsers = personas.data.filter(
    (entry) => entry.kind !== "admin" && entry.organisationId === bootstrap.organisation.id,
  );
  const term = search.trim().toLocaleLowerCase();
  const users = managedUsers.filter((entry) => {
    const role = roles[entry.id] ?? defaultUserRole(entry);
    return [entry.name, userEmail(entry), t(`userManagement.roles.${role}`)]
      .join(" ")
      .toLocaleLowerCase()
      .includes(term);
  });
  const editingUser = managedUsers.find((entry) => entry.id === editingUserId) ?? null;

  const openRoleEditor = (user: DemoPersona) => {
    setEditingUserId(user.id);
    setPendingRole(roles[user.id] ?? defaultUserRole(user));
  };
  const closeRoleEditor = () => setEditingUserId(null);
  const confirmRoleChange = () => {
    if (!editingUser) return;
    setRoles((current) => {
      const next = { ...current, [editingUser.id]: pendingRole };
      saveUserRoles(next);
      return next;
    });
    setNotice(
      t("userManagement.confirmation", {
        name: editingUser.name,
        role: t(`userManagement.roles.${pendingRole}`),
      }),
    );
    closeRoleEditor();
  };

  return (
    <>
      <PageHeading title={t("userManagement.title")} subtitle={t("userManagement.subtitle")} />
      <SearchField
        className="mb-5 max-w-xl"
        controlId="manager.users.search"
        label={t("userManagement.search")}
        onChange={(event) => setSearch(event.target.value)}
        value={search}
      />
      {notice ? (
        <p
          aria-live="polite"
          className="mb-5 rounded-xl border border-success-foreground/15 bg-success px-4 py-3 text-sm font-medium text-success-foreground"
        >
          {notice}
        </p>
      ) : null}

      <Card className="hidden overflow-hidden lg:block" padding="none">
        <table className="w-full table-fixed border-collapse text-left text-xs xl:text-sm">
          <thead className="border-b border-border bg-muted/40 text-muted-foreground">
            <tr>
              <th className="w-[22%] px-3 py-3 font-medium">{t("userManagement.columns.name")}</th>
              <th className="w-[31%] px-3 py-3 font-medium">{t("userManagement.columns.email")}</th>
              <th className="w-[18%] px-3 py-3 font-medium">{t("userManagement.columns.role")}</th>
              <th className="w-[19%] px-3 py-3 font-medium">{t("userManagement.columns.lastLogin")}</th>
              <th className="w-[10%] px-3 py-3">
                <span className="sr-only">{t("userManagement.columns.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => {
              const role = roles[user.id] ?? defaultUserRole(user);
              return (
                <tr
                  className="group transition-colors hover:bg-muted/40 focus-within:bg-muted/40"
                  key={`${user.organisationId}:${user.id}`}
                >
                  <td className="truncate px-3 py-3.5 font-medium">{user.name}</td>
                  <td className="truncate px-3 py-3.5 text-muted-foreground" title={userEmail(user)}>
                    {userEmail(user)}
                  </td>
                  <td className="px-3 py-3.5">
                    <span className="inline-flex rounded-full bg-muted px-2.5 py-1 font-medium text-foreground">
                      {t(`userManagement.roles.${role}`)}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-muted-foreground">{userLastLogin(user)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      aria-label={t("userManagement.editRoleFor", { name: user.name })}
                      className="inline-flex min-h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-primary opacity-0 transition-opacity hover:bg-primary/10 focus:opacity-100 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 group-hover:opacity-100 group-focus-within:opacity-100"
                      data-control-id={`manager.users.edit.${user.id}`}
                      onClick={() => openRoleEditor(user)}
                      type="button"
                    >
                      <Pencil aria-hidden="true" className="size-3.5" />
                      <span className="hidden xl:inline">{t("userManagement.edit")}</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <div className="grid gap-4 lg:hidden">
        {users.map((user) => {
          const role = roles[user.id] ?? defaultUserRole(user);
          return (
            <Card key={`${user.organisationId}:${user.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-medium">{user.name}</h2>
                  <p className="mt-1 truncate text-sm text-muted-foreground">{userEmail(user)}</p>
                </div>
                <button
                  aria-label={t("userManagement.editRoleFor", { name: user.name })}
                  className="inline-flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                  data-control-id={`manager.users.edit.${user.id}.mobile`}
                  onClick={() => openRoleEditor(user)}
                  type="button"
                >
                  <Pencil aria-hidden="true" className="size-4" />
                  {t("userManagement.edit")}
                </button>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">{t("userManagement.columns.role")}</dt>
                  <dd className="font-medium">{t(`userManagement.roles.${role}`)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t("userManagement.columns.lastLogin")}</dt>
                  <dd className="font-medium">{userLastLogin(user)}</dd>
                </div>
              </dl>
            </Card>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 text-sm text-muted-foreground">
        <p>{t("userManagement.resultCount", { count: users.length })}</p>
        <p>{t("userManagement.readOnlyHint")}</p>
      </div>
      {users.length === 0 ? (
        <p className="py-10 text-center text-muted-foreground">{t("userManagement.noResults")}</p>
      ) : null}

      <Modal
        closeLabel={t("common.close")}
        controlId="manager.users.role-dialog"
        onClose={closeRoleEditor}
        open={Boolean(editingUser)}
        title={t("userManagement.dialog.title")}
      >
        {editingUser ? (
          <>
            <p className="text-sm text-muted-foreground">
              {t("userManagement.dialog.description", { name: editingUser.name })}
            </p>
            <label className="mt-5 block text-sm font-medium" htmlFor="manager-user-role">
              {t("userManagement.dialog.role")}
              <select
                className="mt-2 min-h-11 w-full rounded-xl border border-input bg-background px-3 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                data-control-id="manager.users.role-dialog.role"
                id="manager-user-role"
                onChange={(event) => setPendingRole(event.target.value as ManagedUserRole)}
                value={pendingRole}
              >
                <option value="teacher">{t("userManagement.roles.teacher")}</option>
                <option value="manager">{t("userManagement.roles.manager")}</option>
                <option value="mentor">{t("userManagement.roles.mentor")}</option>
              </select>
            </label>
            <p className="mt-5 rounded-xl bg-muted px-4 py-3 text-sm">
              {t("userManagement.dialog.confirmation", {
                name: editingUser.name,
                role: t(`userManagement.roles.${pendingRole}`),
              })}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                controlId="manager.users.role-dialog.cancel"
                onClick={closeRoleEditor}
                variant="secondary"
              >
                {t("common.cancel")}
              </Button>
              <Button controlId="manager.users.role-dialog.confirm" onClick={confirmRoleChange}>
                {t("userManagement.dialog.confirm")}
              </Button>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
}
